/*
 * Simple Bundles BYOB product-page pack detail enrichment.
 *
 * When the app opens dialog.byob-options-modal (image/title click),
 * upgrades it with a multi-image gallery + rich HTML description from
 * /products/{handle}.js. Does NOT touch Add / qty / box-selection.
 *
 * Scoped to build_your-own_box (+ test handle). Not used on homepage builder.
 */
(function () {
  "use strict";

  var HANDLES = ["build_your-own_box", "test-build_your-own_box"];
  var ENRICHED_ATTR = "data-ks-sb-pack-enriched";
  var GALLERY_CLASS = "ks-sb-pack-gallery";
  var DESC_CLASS = "ks-sb-pack-desc";
  var AUTOPLAY_MS = 4000;

  var cache = Object.create(null);
  var activeDialog = null;
  var galleryState = null;
  var autoplayTimer = null;
  var prefersReducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function isTargetPage() {
    try {
      var cfg = window.KS_SB_BYOB_SHIPPING;
      if (cfg && cfg.handle) {
        return HANDLES.indexOf(String(cfg.handle)) !== -1;
      }
      var path = window.location.pathname || "";
      return HANDLES.some(function (h) {
        return (
          path.indexOf("/products/" + h) !== -1 ||
          path.indexOf("/products/" + encodeURIComponent(h)) !== -1
        );
      });
    } catch (_) {
      return false;
    }
  }

  function absolutizeUrl(url) {
    if (!url) return "";
    if (url.indexOf("//") === 0) return window.location.protocol + url;
    return url;
  }

  function imageDedupeKey(url) {
    if (!url) return "";
    try {
      var parsed = new URL(absolutizeUrl(url), window.location.origin);
      var path = parsed.pathname || "";
      path = path
        .replace(/_(?:\d+x\d*|\d+x)(?:@\d+x)?(?=\.[a-zA-Z0-9]+$)/, "")
        .replace(/@\d+x(?=\.[a-zA-Z0-9]+$)/, "");
      return (parsed.hostname + path).toLowerCase();
    } catch (_) {
      return String(url)
        .replace(/[?&](width|height|crop|v)=[^&]*/g, "")
        .replace(/_(?:\d+x\d*|\d+x)(?:@\d+x)?(?=\.[a-zA-Z0-9]+)/, "")
        .toLowerCase();
    }
  }

  function dedupeImages(images) {
    var out = [];
    var seen = Object.create(null);
    (images || []).forEach(function (img) {
      if (!img) return;
      var src = typeof img === "string" ? img : img.src;
      if (!src) return;
      var key = imageDedupeKey(src);
      if (!key || seen[key]) return;
      seen[key] = true;
      if (typeof img === "string") out.push({ src: src, thumb: src, alt: "" });
      else out.push({ src: src, thumb: img.thumb || src, alt: img.alt || "" });
    });
    return out;
  }

  function productJsonUrlFromProductUrl(productUrl) {
    if (!productUrl) return "";
    try {
      var url = new URL(productUrl, window.location.origin);
      var match = url.pathname.match(/\/products\/([^/?#]+)/i);
      if (!match) return "";
      return "/products/" + match[1] + ".js";
    } catch (_) {
      return "";
    }
  }

  function resolveProductUrl(dialog) {
    if (!dialog) return "";
    var card = dialog.closest("byob-product-picker-card");
    var root = card || dialog.parentElement || document;

    var link = dialog.querySelector(".byob-options-modal__details-link[href]");
    if (link && link.getAttribute("href")) {
      return link.getAttribute("href");
    }

    var product = null;
    if (card) {
      var section = card.closest("byob-section") || card.parentElement;
      // Prefer the byob-product that owns this card's variant when possible.
      var variant = card.querySelector("byob-variant") || null;
      if (!variant && section) {
        // Card is usually a sibling tree under byob-section > byob-product.
        product = card.closest("byob-product");
      }
      if (!product) product = card.closest("byob-product");
    }
    if (!product) {
      product = root.querySelector && root.querySelector("byob-product");
    }
    if (product && product.getAttribute("product-url")) {
      return product.getAttribute("product-url");
    }

    // Walk previous siblings / ancestors for byob-product near the card.
    var node = card;
    while (node && node !== document.body) {
      if (node.tagName && node.tagName.toLowerCase() === "byob-product") {
        if (node.getAttribute("product-url")) return node.getAttribute("product-url");
      }
      var prev = node.previousElementSibling;
      while (prev) {
        if (prev.tagName && prev.tagName.toLowerCase() === "byob-product") {
          if (prev.getAttribute("product-url")) return prev.getAttribute("product-url");
        }
        var nested = prev.querySelector && prev.querySelector("byob-product[product-url]");
        if (nested) return nested.getAttribute("product-url");
        prev = prev.previousElementSibling;
      }
      node = node.parentElement;
    }

    // Fallback: match by title against byob-product attributes.
    var titleEl = dialog.querySelector(".byob-options-modal__title");
    var title = titleEl ? titleEl.textContent.trim() : "";
    if (title) {
      var products = document.querySelectorAll("byob-product[product-url]");
      for (var i = 0; i < products.length; i++) {
        if ((products[i].getAttribute("product-title") || "").trim() === title) {
          return products[i].getAttribute("product-url");
        }
      }
    }

    return "";
  }

  function fetchPackDetails(productUrl) {
    if (!productUrl) return Promise.resolve(null);
    if (cache[productUrl]) return Promise.resolve(cache[productUrl]);

    var jsonUrl = productJsonUrlFromProductUrl(productUrl);
    if (!jsonUrl) return Promise.resolve(null);

    return fetch(jsonUrl, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (response) {
        if (!response.ok) throw new Error("product.js " + response.status);
        return response.json();
      })
      .then(function (data) {
        var images = [];
        var media = data.media || [];
        (data.images || []).forEach(function (src, index) {
          var m = media[index];
          var resolved =
            typeof src === "string" ? src : (src && src.src) || "";
          if (!resolved) return;
          images.push({
            src: absolutizeUrl(resolved),
            thumb: absolutizeUrl(resolved),
            alt: (m && m.alt) || data.title || "",
          });
        });
        if (!images.length && data.featured_image) {
          images.push({
            src: absolutizeUrl(data.featured_image),
            thumb: absolutizeUrl(data.featured_image),
            alt: data.title || "",
          });
        }
        var variant =
          (data.variants && data.variants[0]) || null;
        var weightGrams =
          variant && variant.weight != null ? Number(variant.weight) : null;

        var result = {
          title: data.title || "",
          description: data.description || "",
          images: dedupeImages(images),
          weightGrams: Number.isFinite(weightGrams) ? weightGrams : null,
          weightLabel: formatPackWeightLb(weightGrams),
        };
        cache[productUrl] = result;
        return result;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * Shopify Ajax /products/*.js returns variant.weight in grams.
   * Display as pounds to match how packs are sold.
   */
  function formatPackWeightLb(grams) {
    var g = Number(grams);
    if (!Number.isFinite(g) || g <= 0) return "";
    var lbs = g / 453.59237;
    var rounded;
    if (lbs >= 10) {
      rounded = Math.round(lbs);
    } else if (Math.abs(lbs - Math.round(lbs)) < 0.05) {
      rounded = Math.round(lbs);
    } else if (lbs >= 1) {
      rounded = Math.round(lbs * 10) / 10;
    } else {
      rounded = Math.round(lbs * 100) / 100;
    }
    var text = String(parseFloat(Number(rounded).toFixed(2)));
    return text + " lb";
  }

  function stopAutoplay() {
    if (autoplayTimer) {
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    }
  }

  function startAutoplay() {
    stopAutoplay();
    if (prefersReducedMotion) return;
    if (!galleryState || galleryState.images.length < 2) return;
    if (galleryState.userPaused) return;
    autoplayTimer = window.setInterval(function () {
      if (!galleryState || galleryState.userPaused) return;
      showGalleryImage(galleryState.index + 1);
    }, AUTOPLAY_MS);
  }

  function pauseAutoplayForUser() {
    if (!galleryState) return;
    galleryState.userPaused = true;
    stopAutoplay();
  }

  function showGalleryImage(index) {
    if (!galleryState || !galleryState.images.length) return;
    var len = galleryState.images.length;
    galleryState.index = ((index % len) + len) % len;
    var image = galleryState.images[galleryState.index];
    if (galleryState.mainImg) {
      galleryState.mainImg.src = image.src;
      galleryState.mainImg.alt = image.alt || "";
    }
    if (galleryState.thumbsRoot) {
      var thumbs = galleryState.thumbsRoot.querySelectorAll(
        "[data-ks-sb-pack-gallery-thumb]"
      );
      for (var i = 0; i < thumbs.length; i++) {
        var isActive =
          parseInt(thumbs[i].getAttribute("data-index") || "-1", 10) ===
          galleryState.index;
        thumbs[i].classList.toggle("is-active", isActive);
        thumbs[i].setAttribute("aria-selected", isActive ? "true" : "false");
      }
    }
  }

  function buildGallery(images, fallbackImg) {
    var wrap = document.createElement("div");
    wrap.className = GALLERY_CLASS;
    wrap.setAttribute("data-ks-sb-pack-gallery", "");

    var main = document.createElement("div");
    main.className = GALLERY_CLASS + "__main";
    main.setAttribute("data-ks-sb-pack-gallery-main", "");

    // Reuse the already-painted SB hero <img> when possible — avoids blank flash.
    var img = null;
    var reused = false;
    if (fallbackImg && fallbackImg.tagName === "IMG") {
      img = fallbackImg;
      reused = true;
      img.hidden = false;
      img.removeAttribute("hidden");
      img.classList.remove("ks-sb-pack-native-hero");
      img.removeAttribute("data-ks-sb-pack-original-img");
      img.classList.add(GALLERY_CLASS + "__image");
      img.setAttribute("data-ks-sb-pack-gallery-image", "");
      var paintedSrc = img.currentSrc || img.src || "";
      if (paintedSrc && images.length) {
        // Keep the painted URL as slide 0 when it's the same asset.
        if (imageDedupeKey(paintedSrc) === imageDedupeKey(images[0].src)) {
          images[0] = {
            src: paintedSrc,
            thumb: images[0].thumb || paintedSrc,
            alt: images[0].alt || img.alt || "",
          };
        } else {
          // Different first asset — prepend painted hero so nothing blanks.
          images = dedupeImages(
            [
              {
                src: paintedSrc,
                thumb: paintedSrc,
                alt: img.alt || (images[0] && images[0].alt) || "",
              },
            ].concat(images)
          );
        }
      } else if (paintedSrc && !images.length) {
        images = [{ src: paintedSrc, thumb: paintedSrc, alt: img.alt || "" }];
      }
      if (!img.getAttribute("width")) img.width = 900;
      if (!img.getAttribute("height")) img.height = 675;
    } else {
      img = document.createElement("img");
      img.className = GALLERY_CLASS + "__image";
      img.setAttribute("data-ks-sb-pack-gallery-image", "");
      img.decoding = "async";
      img.alt = (images[0] && images[0].alt) || "";
      img.src = (images[0] && images[0].src) || "";
      img.width = 900;
      img.height = 675;
    }
    main.appendChild(img);

    var multi = images.length > 1;
    var prevBtn = null;
    var nextBtn = null;
    if (multi) {
      prevBtn = document.createElement("button");
      prevBtn.type = "button";
      prevBtn.className =
        GALLERY_CLASS + "__nav " + GALLERY_CLASS + "__nav--prev";
      prevBtn.setAttribute("aria-label", "Previous image");
      prevBtn.innerHTML =
        '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12.7 4.3a1 1 0 010 1.4L8.4 10l4.3 4.3a1 1 0 11-1.4 1.4l-5-5a1 1 0 010-1.4l5-5a1 1 0 011.4 0z"/></svg>';
      main.appendChild(prevBtn);

      nextBtn = document.createElement("button");
      nextBtn.type = "button";
      nextBtn.className =
        GALLERY_CLASS + "__nav " + GALLERY_CLASS + "__nav--next";
      nextBtn.setAttribute("aria-label", "Next image");
      nextBtn.innerHTML =
        '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path fill="currentColor" d="M7.3 15.7a1 1 0 010-1.4L11.6 10 7.3 5.7a1 1 0 111.4-1.4l5 5a1 1 0 010 1.4l-5 5a1 1 0 01-1.4 0z"/></svg>';
      main.appendChild(nextBtn);
    }

    wrap.appendChild(main);

    var thumbsRoot = null;
    if (multi) {
      thumbsRoot = document.createElement("div");
      thumbsRoot.className = GALLERY_CLASS + "__thumbs";
      thumbsRoot.setAttribute("role", "listbox");
      thumbsRoot.setAttribute("aria-label", "Pack images");
      images.forEach(function (image, index) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = GALLERY_CLASS + "__thumb";
        if (index === 0) btn.classList.add("is-active");
        btn.setAttribute("data-ks-sb-pack-gallery-thumb", "");
        btn.setAttribute("data-index", String(index));
        btn.setAttribute("role", "option");
        btn.setAttribute("aria-selected", index === 0 ? "true" : "false");
        btn.setAttribute("aria-label", "Image " + (index + 1));
        var thumbImg = document.createElement("img");
        thumbImg.src = image.thumb || image.src;
        thumbImg.alt = "";
        thumbImg.decoding = "async";
        thumbImg.loading = "lazy";
        btn.appendChild(thumbImg);
        thumbsRoot.appendChild(btn);
      });
      wrap.appendChild(thumbsRoot);
    }

    galleryState = {
      images: images,
      index: 0,
      mainImg: img,
      thumbsRoot: thumbsRoot,
      userPaused: false,
      touchStartX: null,
      reusedNativeImg: reused,
    };

    if (prevBtn) {
      prevBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        pauseAutoplayForUser();
        showGalleryImage(galleryState.index - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        pauseAutoplayForUser();
        showGalleryImage(galleryState.index + 1);
      });
    }
    if (thumbsRoot) {
      thumbsRoot.addEventListener("click", function (e) {
        var thumb = e.target.closest("[data-ks-sb-pack-gallery-thumb]");
        if (!thumb) return;
        e.preventDefault();
        e.stopPropagation();
        var idx = parseInt(thumb.getAttribute("data-index") || "0", 10);
        pauseAutoplayForUser();
        showGalleryImage(idx);
      });
    }

    main.addEventListener("mouseenter", function () {
      if (!galleryState) return;
      galleryState._hoverPaused = true;
      stopAutoplay();
    });
    main.addEventListener("mouseleave", function () {
      if (!galleryState) return;
      galleryState._hoverPaused = false;
      if (!galleryState.userPaused) startAutoplay();
    });
    main.addEventListener(
      "touchstart",
      function (e) {
        if (!galleryState) return;
        galleryState.touchStartX =
          (e.changedTouches && e.changedTouches[0] && e.changedTouches[0].clientX) ||
          null;
      },
      { passive: true }
    );
    main.addEventListener(
      "touchend",
      function (e) {
        if (!galleryState || galleryState.touchStartX == null) return;
        var endX =
          e.changedTouches && e.changedTouches[0] && e.changedTouches[0].clientX;
        if (endX == null) return;
        var delta = endX - galleryState.touchStartX;
        galleryState.touchStartX = null;
        if (Math.abs(delta) < 40) return;
        pauseAutoplayForUser();
        showGalleryImage(galleryState.index + (delta < 0 ? 1 : -1));
      },
      { passive: true }
    );

    return wrap;
  }

  function clearLoading(dialog) {
    if (!dialog) return;
    var el = dialog.querySelector("[data-ks-sb-pack-loading]");
    if (el) el.remove();
    var descLoading = dialog.querySelector("[data-ks-sb-pack-desc-loading]");
    if (descLoading) descLoading.remove();
    dialog.classList.remove("ks-sb-pack-modal--loading");
  }

  /**
   * Apply our layout shell immediately (sync) so the native SB modal
   * never paints in its old cramped layout before enrichment finishes.
   * No "Loading…" copy — keep the native image/description until rich data arrives.
   */
  function prepareDialogShell(dialog) {
    if (!dialog) return;
    dialog.classList.add("ks-sb-pack-modal--enriched");

    var detailsLink = dialog.querySelector(".byob-options-modal__details-link");
    if (detailsLink) {
      detailsLink.hidden = true;
      detailsLink.setAttribute("aria-hidden", "true");
    }

    var inner = dialog.querySelector(".byob-options-modal__inner");
    if (inner) inner.classList.add("ks-sb-pack-modal__inner");
    var content = dialog.querySelector(".byob-options-modal__content");
    if (content) content.classList.add("ks-sb-pack-modal__content");
    var header = dialog.querySelector(".byob-options-modal__header");
    if (header) header.classList.add("ks-sb-pack-modal__header");
    var selectors = dialog.querySelector(".byob-options-modal__selectors");
    if (selectors) selectors.classList.add("ks-sb-pack-modal__selectors");

    // Stretch the native hero image full-width until the gallery adopts it.
    var media = dialog.querySelector(".byob-options-modal__media");
    var nativeImg =
      media &&
      media.querySelector(
        "img:not([data-ks-sb-pack-gallery-image]):not([data-ks-sb-pack-original-img])"
      );
    if (nativeImg) {
      nativeImg.classList.add("ks-sb-pack-native-hero");
    }
  }

  function applyEnrichment(dialog, details) {
    if (!dialog || !details) return;

    prepareDialogShell(dialog);
    dialog.setAttribute(ENRICHED_ATTR, "1");
    clearLoading(dialog);

    var detailsLink = dialog.querySelector(".byob-options-modal__details-link");
    if (detailsLink) {
      detailsLink.hidden = true;
      detailsLink.setAttribute("aria-hidden", "true");
    }

    var media = dialog.querySelector(".byob-options-modal__media");
    if (media && details.images && details.images.length) {
      var existingGallery = media.querySelector("[data-ks-sb-pack-gallery]");
      if (existingGallery) existingGallery.remove();
      var fallbackImg = media.querySelector(
        "img.ks-sb-pack-native-hero, img:not([data-ks-sb-pack-gallery-image])"
      );
      var gallery = buildGallery(details.images.slice(), fallbackImg);
      // buildGallery moves/reuses fallbackImg into the gallery — do not hide it.
      media.insertBefore(gallery, media.firstChild);
      startAutoplay();
    }

    var descEl = dialog.querySelector(".byob-options-modal__desc");
    if (descEl && details.description) {
      dialog.querySelectorAll("." + DESC_CLASS).forEach(function (node) {
        node.remove();
      });
      var rich = document.createElement("div");
      rich.className = DESC_CLASS + " rte";
      rich.innerHTML = details.description;
      descEl.hidden = true;
      descEl.setAttribute("aria-hidden", "true");
      descEl.insertAdjacentElement("afterend", rich);
    }

    var priceEl = dialog.querySelector(".byob-options-modal__price");
    if (priceEl) {
      var oldWeight = priceEl.querySelector(".ks-sb-pack-weight");
      if (oldWeight) oldWeight.remove();
      if (details.weightLabel) {
        var weightEl = document.createElement("span");
        weightEl.className = "ks-sb-pack-weight";
        weightEl.textContent = "· " + details.weightLabel;
        priceEl.appendChild(weightEl);
      }
    }

    var inner = dialog.querySelector(".byob-options-modal__inner");
    if (inner) inner.classList.add("ks-sb-pack-modal__inner");
    var content = dialog.querySelector(".byob-options-modal__content");
    if (content) content.classList.add("ks-sb-pack-modal__content");
    var header = dialog.querySelector(".byob-options-modal__header");
    if (header) header.classList.add("ks-sb-pack-modal__header");
    var selectors = dialog.querySelector(".byob-options-modal__selectors");
    if (selectors) selectors.classList.add("ks-sb-pack-modal__selectors");
  }

  function onDialogOpen(dialog) {
    if (!dialog || !dialog.classList.contains("byob-options-modal")) return;
    if (!dialog.open) return;

    // One pack detail at a time — prevents stacked ghost dialogs.
    closeOtherOpenDialogs(dialog);

    // Instant shell so the native SB layout never flashes.
    prepareDialogShell(dialog);

    activeDialog = dialog;

    // Already enriched on a prior open — rehydrate gallery controls + resume autoplay.
    if (dialog.getAttribute(ENRICHED_ATTR) === "1") {
      clearLoading(dialog);
      dialog.classList.remove("ks-sb-pack-modal--loading");
      var hasWeight = !!dialog.querySelector(".ks-sb-pack-weight");
      if (rehydrateGalleryFromDom(dialog) && hasWeight) {
        if (!galleryState.userPaused) startAutoplay();
        return;
      }
      // Missing gallery state or weight label — allow a fresh enrich (uses cache).
      dialog.removeAttribute(ENRICHED_ATTR);
    }

    if (dialog.getAttribute("data-ks-sb-pack-fetching") === "1") return;

    dialog.setAttribute("data-ks-sb-pack-fetching", "1");

    var productUrl = resolveProductUrl(dialog);
    var requestToken = String(Date.now()) + Math.random();
    dialog.setAttribute("data-ks-sb-pack-request", requestToken);

    fetchPackDetails(productUrl).then(function (details) {
      if (dialog.getAttribute("data-ks-sb-pack-request") !== requestToken) return;
      dialog.removeAttribute("data-ks-sb-pack-fetching");
      if (!dialog.open) return;
      clearLoading(dialog);
      if (!details) return;
      applyEnrichment(dialog, details);
    });
  }

  function rehydrateGalleryFromDom(dialog) {
    var gallery = dialog.querySelector("[data-ks-sb-pack-gallery]");
    if (!gallery) return false;
    var mainImg = gallery.querySelector("[data-ks-sb-pack-gallery-image]");
    var thumbsRoot = gallery.querySelector("." + GALLERY_CLASS + "__thumbs");
    var thumbs = gallery.querySelectorAll("[data-ks-sb-pack-gallery-thumb]");
    var images = [];
    if (thumbs.length) {
      for (var i = 0; i < thumbs.length; i++) {
        var tImg = thumbs[i].querySelector("img");
        images.push({
          src: (tImg && tImg.src) || "",
          thumb: (tImg && tImg.src) || "",
          alt: mainImg ? mainImg.alt || "" : "",
        });
      }
    } else if (mainImg && mainImg.src) {
      images.push({ src: mainImg.src, thumb: mainImg.src, alt: mainImg.alt || "" });
    }
    images = dedupeImages(images);
    if (!images.length) return false;

    var activeIndex = 0;
    for (var j = 0; j < thumbs.length; j++) {
      if (thumbs[j].classList.contains("is-active")) {
        activeIndex = j;
        break;
      }
    }

    galleryState = {
      images: images,
      index: activeIndex,
      mainImg: mainImg,
      thumbsRoot: thumbsRoot,
      userPaused: false,
      touchStartX: null,
    };
    return true;
  }

  function closeOtherOpenDialogs(except) {
    var openDialogs = document.querySelectorAll("dialog.byob-options-modal[open]");
    for (var i = 0; i < openDialogs.length; i++) {
      var d = openDialogs[i];
      if (except && d === except) continue;
      try {
        if (typeof d.close === "function") d.close();
        else d.removeAttribute("open");
      } catch (_) {
        d.removeAttribute("open");
      }
      onDialogClose(d);
    }
  }

  function onDialogClose(dialog) {
    if (activeDialog === dialog) activeDialog = null;
    stopAutoplay();
    clearLoading(dialog);
    if (dialog) {
      dialog.removeAttribute("data-ks-sb-pack-fetching");
      // Belt-and-suspenders: never leave a closed enriched dialog interactive.
      if (!dialog.open) {
        dialog.style.removeProperty("display");
      }
    }
    // Keep enrichment + gallery DOM for instant reopen; drop runtime state only.
    galleryState = null;
  }

  function onKeydown(e) {
    if (!activeDialog || !activeDialog.open) return;
    if (!galleryState || galleryState.images.length < 2) return;
    if (e.code === "ArrowLeft") {
      e.preventDefault();
      pauseAutoplayForUser();
      showGalleryImage(galleryState.index - 1);
    } else if (e.code === "ArrowRight") {
      e.preventDefault();
      pauseAutoplayForUser();
      showGalleryImage(galleryState.index + 1);
    }
  }

  function bindDialog(dialog) {
    if (!dialog || dialog.getAttribute("data-ks-sb-pack-bound")) return;
    dialog.setAttribute("data-ks-sb-pack-bound", "1");

    dialog.addEventListener("toggle", function () {
      if (dialog.open) onDialogOpen(dialog);
      else onDialogClose(dialog);
    });

    // Some browsers / polyfills may not fire toggle reliably.
    var mo = new MutationObserver(function () {
      if (dialog.open) onDialogOpen(dialog);
      else onDialogClose(dialog);
    });
    mo.observe(dialog, { attributes: true, attributeFilter: ["open"] });

    if (dialog.open) onDialogOpen(dialog);
  }

  function scanDialogs(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var dialogs = scope.querySelectorAll
      ? scope.querySelectorAll("dialog.byob-options-modal")
      : [];
    for (var i = 0; i < dialogs.length; i++) bindDialog(dialogs[i]);

    // Also bind if root itself is a dialog.
    if (
      root &&
      root.tagName === "DIALOG" &&
      root.classList &&
      root.classList.contains("byob-options-modal")
    ) {
      bindDialog(root);
    }
  }

  function init() {
    if (!isTargetPage()) return;

    scanDialogs(document);

    // Capture-phase: restyle the dialog shell before SB paints the old layout.
    document.addEventListener(
      "click",
      function (event) {
        var trigger = event.target.closest(
          ".byob-bundle__product-card-media--clickable, .byob-bundle__product-card-title--clickable"
        );
        if (!trigger) return;
        var card = trigger.closest("byob-product-picker-card");
        if (!card) return;
        var dialog = card.querySelector("dialog.byob-options-modal");
        if (!dialog) return;
        prepareDialogShell(dialog);
      },
      true
    );

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (!node || node.nodeType !== 1) continue;
          if (
            node.matches &&
            node.matches("dialog.byob-options-modal, byob-product-picker-card, byob-bundle, byob-section")
          ) {
            scanDialogs(node);
          } else if (node.querySelector) {
            if (node.querySelector("dialog.byob-options-modal")) scanDialogs(node);
          }
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener("keydown", onKeydown);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
